import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { fileUrl } from "../lib/seo";
import { useAuth } from "../contexts/AuthContext";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Star, BadgeCheck, ImagePlus, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTOS = 4;
const photosOf = (r) => (r.photo_urls?.length ? r.photo_urls : r.photo_url ? [r.photo_url] : []);

export function Stars({ value, size = "w-4 h-4", onPick }) {
  return (
    <span className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} data-testid={onPick ? `star-pick-${i}` : undefined} onClick={onPick ? () => onPick(i) : undefined}
          className={`${size} ${i <= Math.round(value) ? "fill-[#E65100] text-[#E65100]" : "text-[#D4AF37]/60"} ${onPick ? "cursor-pointer" : ""}`} />
      ))}
    </span>
  );
}

export default function ProductReviews({ productId, onChange }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [viewer, setViewer] = useState(null);
  const load = () => api.get(`/shop/products/${productId}/reviews`).then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps
  const mine = user && items.find((r) => r.user_id === user.id);
  const refresh = () => { load(); onChange?.(); };
  const gallery = items.flatMap((r) => photosOf(r).map((url) => ({ url, review: r })));
  const openAt = (url) => setViewer(gallery.findIndex((g) => g.url === url));

  return (
    <section data-testid="product-reviews" className="mt-16">
      <h2 className="font-serif text-3xl text-[#4A0E17]">Reviews ({items.length})</h2>
      <PhotoStrip gallery={gallery} onOpen={setViewer} />
      {user ? <ReviewForm productId={productId} existing={mine} onSaved={refresh} />
        : <p className="mt-4 text-sm text-[#63534E]"><Link to="/login" className="text-[#E65100] font-semibold">Sign in</Link> to write a review.</p>}
      <div className="mt-8 space-y-5">
        {items.length === 0 && <p className="text-[#63534E] text-sm">No reviews yet. Be the first to share your experience.</p>}
        {items.map((r) => <ReviewCard key={r.id} r={r} onOpen={openAt} />)}
      </div>
      <Lightbox gallery={gallery} idx={viewer} setIdx={setViewer} />
    </section>
  );
}

function PhotoStrip({ gallery, onOpen }) {
  if (!gallery.length) return null;
  return (
    <div data-testid="review-photo-strip" className="mt-5">
      <div className="overline">Photos from devotees ({gallery.length})</div>
      <div className="mt-3 flex gap-3 overflow-x-auto pb-2 snap-x">
        {gallery.map((g, i) => (
          <button key={`${g.url}-${i}`} data-testid={`strip-photo-${i}`} onClick={() => onOpen(i)} className="snap-start shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 border-transparent hover:border-[#E65100] transition-sacred">
            <img src={fileUrl(g.url)} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

function ReviewCard({ r, onOpen }) {
  return (
    <div data-testid={`review-${r.id}`} className="bg-[#FFFDF9] sacred-border rounded-2xl p-5">
      <div className="flex flex-wrap items-center gap-3">
        <Stars value={r.rating} />
        <span className="font-semibold text-[#4A0E17]">{r.user_name}</span>
        {r.verified_purchase && <span className="inline-flex items-center gap-1 text-xs text-green-800 bg-green-50 rounded-full px-2 py-0.5"><BadgeCheck className="w-3 h-3" /> Verified purchase</span>}
        <span className="text-xs text-[#8C6D3B] ml-auto">{new Date(r.updated_at || r.created_at).toLocaleDateString()}</span>
      </div>
      <p className="text-[#1F1412] mt-3 whitespace-pre-line">{r.text}</p>
      {photosOf(r).length > 0 && (
        <div className="mt-3 flex gap-2">
          {photosOf(r).map((u) => <button key={u} onClick={() => onOpen(u)}><img src={fileUrl(u)} alt="" className="w-20 h-20 rounded-lg object-cover hover:opacity-80" /></button>)}
        </div>
      )}
    </div>
  );
}

function Lightbox({ gallery, idx, setIdx }) {
  const open = idx !== null && idx >= 0 && idx < gallery.length;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => Math.min(gallery.length - 1, i + 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, gallery.length, setIdx]);
  if (!open) return null;
  const { url, review } = gallery[idx];
  return (
    <Dialog open onOpenChange={(o) => !o && setIdx(null)}>
      <DialogContent data-testid="review-lightbox" className="max-w-4xl bg-[#1A0508] border-[#D4AF37]/40 text-[#FAF6F0] p-0 overflow-hidden [&>button]:hidden">
        <DialogTitle className="sr-only">Review photo</DialogTitle>
        <div className="relative bg-black flex items-center justify-center h-[60vh]">
          <img src={fileUrl(url)} alt="" className="max-h-full max-w-full object-contain" />
          <button data-testid="lightbox-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="absolute left-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center disabled:opacity-20"><ChevronLeft className="w-5 h-5" /></button>
          <button data-testid="lightbox-next" disabled={idx === gallery.length - 1} onClick={() => setIdx(idx + 1)} className="absolute right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center disabled:opacity-20"><ChevronRight className="w-5 h-5" /></button>
          <button data-testid="lightbox-close" onClick={() => setIdx(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center"><X className="w-4 h-4" /></button>
          <span data-testid="lightbox-pos" className="absolute bottom-3 left-1/2 -translate-x-1/2 font-mono text-xs bg-black/50 px-2 py-0.5 rounded">{idx + 1} / {gallery.length}</span>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3"><Stars value={review.rating} /><span className="font-semibold">{review.user_name}</span>
            {review.verified_purchase && <span className="text-xs text-green-300">Verified purchase</span>}</div>
          <p className="text-sm text-[#FAF6F0]/80 mt-2 line-clamp-3">{review.text}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReviewForm({ productId, existing, onSaved }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (existing) { setRating(existing.rating); setText(existing.text); setPhotos(photosOf(existing)); } }, [existing]);

  const upload = async (e) => {
    const files = [...(e.target.files || [])].slice(0, MAX_PHOTOS - photos.length);
    e.target.value = "";
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post("/uploads/image", fd); setPhotos((p) => [...p, data.url].slice(0, MAX_PHOTOS)); }
      catch (err) { toast.error(formatError(err)); }
    }
  };
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/shop/products/${productId}/reviews`, { rating, text, photo_urls: photos }); toast.success("Thank you for your review"); onSaved(); }
    catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} data-testid="review-form" className="mt-6 bg-[#FFFDF9] sacred-border rounded-2xl p-5 space-y-3">
      <div className="flex items-center gap-3"><span className="text-sm text-[#4A0E17] font-semibold">{existing ? "Update your review" : "Your rating"}</span><Stars value={rating} size="w-6 h-6" onPick={setRating} /></div>
      <Textarea data-testid="review-text" required minLength={3} rows={3} placeholder="How was the quality, packaging and delivery?" value={text} onChange={(e) => setText(e.target.value)} className="bg-white border-[#D4AF37]/40" />
      <div className="flex flex-wrap items-center gap-3">
        {photos.map((u) => (
          <div key={u} className="relative">
            <img src={fileUrl(u)} alt="" className="w-14 h-14 rounded-lg object-cover" />
            <button type="button" data-testid="review-photo-remove" onClick={() => setPhotos(photos.filter((x) => x !== u))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#4A0E17] text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
          </div>
        ))}
        {photos.length < MAX_PHOTOS && (
          <label className="inline-flex items-center gap-2 text-sm text-[#8C6D3B] cursor-pointer"><ImagePlus className="w-4 h-4" /> Add photos ({photos.length}/{MAX_PHOTOS})
            <input data-testid="review-photo" type="file" accept="image/*" multiple onChange={upload} className="hidden" /></label>
        )}
        <Button data-testid="review-submit" disabled={busy} className="ml-auto bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-10 px-6">{busy ? "Saving…" : existing ? "Update" : "Post review"}</Button>
      </div>
    </form>
  );
}
